import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import { addOptimizationLines } from "#modules/optimizations/infra/addOptimizationLines.api";
import { invalidateQuoteRequestLineDetails } from "#modules/optimizations/infra/invalidateQuoteRequestLineDetails";
import {
  getOptimizationsQueryData,
  optimizationsQuery,
  setOptimizationsQueryData,
} from "#modules/optimizations/infra/optimizations.query";
import { withPreservedCompatibleLines } from "#modules/optimizations/infra/preserveCompatibleLines";
import { quoteRequestLinesQuery } from "#modules/quoteRequestLines/quoteRequestLinesQuery";
import { quoteRequestQuery } from "#modules/quoteRequests/infra/quoteRequest.query";
import { queryKeys } from "#shared/queryKeys/queryKeys";
import { sumMachineTimes } from "shared";

export const useAddOptimizationLines = ({ quoteRequestId }: { quoteRequestId: string }) => {
  const queryClient = useQueryClient();
  const { queryKey } = optimizationsQuery({ quoteRequestId });

  const { mutate, mutateAsync, ...rest } = useMutation({
    mutationFn: addOptimizationLines,
    onMutate: async ({ optimizationId, lineIds }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousOptimization = getOptimizationsQueryData(queryClient, quoteRequestId)?.find(
        (optimization) => optimization.id === optimizationId,
      );
      const addedLineIds = new Set(lineIds);

      setOptimizationsQueryData(queryClient, quoteRequestId, (oldData) =>
        oldData?.map((optimization) => {
          if (optimization.id !== optimizationId) {
            return optimization;
          }

          const addedLines = optimization.compatibleLines.filter((line) =>
            addedLineIds.has(line.id),
          );
          const quoteRequestLines = [...optimization.quoteRequestLines, ...addedLines];
          const compatibleLines = optimization.compatibleLines.filter(
            (line) => !addedLineIds.has(line.id),
          );
          const metadata = optimization.machineTimesOptimizationMetadata;

          if (!metadata) {
            return { ...optimization, quoteRequestLines, compatibleLines };
          }

          const resetTimes = sumMachineTimes({
            lines: quoteRequestLines,
            industrialProcess: metadata.companyMachine.machine.industrialProcess,
            companyMachineId: metadata.companyMachineId,
          });

          return {
            ...optimization,
            quoteRequestLines,
            compatibleLines,
            machineTimesOptimizationMetadata: {
              ...metadata,
              ...resetTimes,
            },
          };
        }),
      );

      return { previousOptimization };
    },
    onError: (err: Error, { optimizationId }, context) => {
      setOptimizationsQueryData(queryClient, quoteRequestId, (oldData) =>
        oldData?.map((optimization) =>
          optimization.id === optimizationId && context?.previousOptimization
            ? context.previousOptimization
            : optimization,
        ),
      );

      showErrorToast({ title: err.message });
      errorHandler(err);
    },
    onSuccess: (updatedOptimization, { lineIds }) => {
      const addedLineIds = new Set(lineIds);

      setOptimizationsQueryData(queryClient, quoteRequestId, (oldData) =>
        oldData?.map((optimization) => {
          if (optimization.id !== updatedOptimization.id) {
            return optimization;
          }

          const preserved = withPreservedCompatibleLines(optimization, updatedOptimization);

          return {
            ...preserved,
            compatibleLines: preserved.compatibleLines.filter((line) => !addedLineIds.has(line.id)),
          };
        }),
      );
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.optimizations.enabledByQuoteRequest(quoteRequestId),
      });
      void queryClient.invalidateQueries(quoteRequestQuery(quoteRequestId));
      void queryClient.invalidateQueries(quoteRequestLinesQuery());
      invalidateQuoteRequestLineDetails(queryClient, lineIds);
    },
  });

  return { addOptimizationLines: mutate, addOptimizationLinesAsync: mutateAsync, ...rest };
};
